import {
  Area,
  AreaChart,
  CartesianGrid,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  type TooltipProps,
} from 'recharts';
import { Card } from '../../ui';
import { fmtCompact, fmtMoney } from '../../utils/format';
import {
  cohortLabel,
  type CohortDimension,
  type CohortSplit,
  type PercentileRow,
  type ScfDataset,
} from '../../cohort/schema';
import { getRow, lookupPercentile } from '../../cohort/lookup';
import { dimensionLabel } from '../../cohort/user';

interface Props {
  dataset: ScfDataset;
  dimension: CohortDimension;
  split: CohortSplit;
  userValue: number;
  /** Optional override title. Defaults to "<dimension> — <splitLabel>". */
  title?: string;
}

const PCTS = [10, 25, 50, 75, 90, 95, 99] as const;

/**
 * Build a population-density curve from the 7 SCF anchor percentiles.
 *
 * Between consecutive anchors p_i and p_{i+1}, (p_{i+1} − p_i)% of all
 * households live in the dollar interval (v_i, v_{i+1}). Density on a
 * log-dollar axis is that fraction divided by the log-span of the interval.
 * We plot one density point at each interval's geometric midpoint, then
 * pad with zero-density endpoints so the curve closes visually.
 *
 * Non-positive anchors (e.g. net-worth p10 in the under-35 cohort) are
 * dropped — log scale can't represent them. The caller surfaces a note
 * when this clipping happens.
 */
function pdfPoints(row: PercentileRow): { x: number; density: number }[] {
  const vals = [row.p10, row.p25, row.p50, row.p75, row.p90, row.p95, row.p99];
  const out: { x: number; density: number }[] = [];
  for (let i = 0; i < PCTS.length - 1; i++) {
    const lo = vals[i];
    const hi = vals[i + 1];
    if (lo <= 0 || hi <= 0 || hi <= lo) continue;
    const popFrac = (PCTS[i + 1] - PCTS[i]) / 100;
    const logSpan = Math.log(hi) - Math.log(lo);
    const density = popFrac / logSpan;
    const x = Math.sqrt(lo * hi);
    out.push({ x, density });
  }
  if (out.length === 0) return out;
  // Visual closure: dip back to ~zero just outside the observed range.
  const first = out[0];
  const last = out[out.length - 1];
  return [
    { x: first.x / 2, density: 0 },
    ...out,
    { x: last.x * 2, density: 0 },
  ];
}

function PercentileTooltip({ active, payload }: TooltipProps<number, string>) {
  if (!active || !payload?.length) return null;
  const p = payload[0].payload as { x: number };
  return (
    <div className="rounded-lg border border-ink-200 bg-white px-2 py-1 text-xs shadow-card dark:border-ink-700 dark:bg-ink-800">
      <span className="text-ink-600 dark:text-ink-300">~{fmtMoney(p.x)}</span>
    </div>
  );
}

/** Pick a few human-readable tick positions on the log axis, anchored to
 *  recognizable percentiles (p10/p50/p90/p99) when they fall inside the domain. */
function logTicks(row: PercentileRow, lo: number, hi: number): number[] {
  const candidates = [row.p10, row.p25, row.p50, row.p75, row.p90, row.p99].filter(
    v => v >= lo && v <= hi && v > 0,
  );
  // De-duplicate while preserving order.
  const seen = new Set<number>();
  return candidates.filter(v => (seen.has(v) ? false : (seen.add(v), true)));
}

function ordinalSuffix(n: number): string {
  const v = Math.round(n);
  const last = v % 10;
  const lastTwo = v % 100;
  if (lastTwo >= 11 && lastTwo <= 13) return `${v}th`;
  if (last === 1) return `${v}st`;
  if (last === 2) return `${v}nd`;
  if (last === 3) return `${v}rd`;
  return `${v}th`;
}

export function PercentileCard({ dataset, dimension, split, userValue, title }: Props) {
  const row = getRow(dataset, split, dimension);
  if (!row) {
    return (
      <Card className="p-4">
        <h3 className="text-sm font-semibold text-ink-800 dark:text-ink-100">
          {title ?? dimensionLabel(dimension)}
        </h3>
        <p className="mt-2 text-xs text-ink-500">
          No data for {cohortLabel(split)} — cohort may be too small in SCF.
        </p>
      </Card>
    );
  }

  const lookup = lookupPercentile(userValue, row);
  const data = pdfPoints(row);
  const hasNegativeTail = row.p10 <= 0 || row.p25 <= 0;

  // Clamp the user marker to the visible domain so the vertical line
  // doesn't escape off-canvas. If their value sits below the lowest
  // positive anchor, pin it to the leftmost x; if above the highest, pin right.
  const xMin = data[0]?.x ?? 1;
  const xMax = data[data.length - 1]?.x ?? 1;
  const markerX = Math.max(xMin, Math.min(xMax, userValue > 0 ? userValue : xMin));

  const headline =
    lookup.clamped === 'above'
      ? '> 99th pct'
      : lookup.clamped === 'below'
        ? '< 10th pct'
        : `${ordinalSuffix(lookup.percentile)} pct`;

  const headlineTone =
    lookup.clamped === 'above'
      ? 'text-accent-700 dark:text-accent-400'
      : lookup.percentile >= 75
        ? 'text-pos-700 dark:text-pos-500'
        : lookup.percentile <= 25
          ? 'text-neg-700 dark:text-neg-500'
          : 'text-ink-800 dark:text-ink-100';

  return (
    <Card className="p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="truncate text-sm font-semibold text-ink-800 dark:text-ink-100">
            {title ?? dimensionLabel(dimension)}
          </h3>
          <p className="text-xs text-ink-500 dark:text-ink-300">
            vs {cohortLabel(split)}
          </p>
        </div>
        <div className="shrink-0 text-right">
          <div className={`text-lg font-bold tabular-nums ${headlineTone}`}>{headline}</div>
          <div className="text-[11px] text-ink-500 dark:text-ink-400">
            you: {fmtMoney(userValue)}
          </div>
        </div>
      </div>

      <div className="mt-3 h-32 w-full">
        {data.length === 0 ? (
          <div className="flex h-full items-center justify-center text-xs text-ink-500">
            Distribution undefined — cohort has no positive values.
          </div>
        ) : (
          <ResponsiveContainer>
            <AreaChart data={data} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
              <CartesianGrid stroke="#eef0f4" strokeDasharray="3 6" vertical={false} />
              <XAxis
                dataKey="x"
                type="number"
                scale="log"
                domain={[xMin, xMax]}
                ticks={logTicks(row, xMin, xMax)}
                tick={{ fill: '#828a9c', fontSize: 10 }}
                tickFormatter={v => fmtCompact(v)}
                tickLine={false}
                axisLine={{ stroke: '#dde1e9' }}
              />
              <YAxis hide />
              <Tooltip content={<PercentileTooltip />} cursor={{ stroke: '#94a3b8', strokeDasharray: '3 3' }} />
              <Area
                type="monotone"
                dataKey="density"
                stroke="#94a3b8"
                fill="#cbd5e1"
                fillOpacity={0.4}
                strokeWidth={1.5}
                isAnimationActive={false}
              />
              <ReferenceLine
                x={markerX}
                stroke="#0ea5e9"
                strokeWidth={2}
                label={{
                  value: 'you',
                  position: 'top',
                  fill: '#0ea5e9',
                  fontSize: 10,
                  fontWeight: 600,
                }}
              />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </div>

      <div className="mt-1 flex justify-between text-[10px] tabular-nums text-ink-500 dark:text-ink-400">
        <span>p10 {fmtCompact(row.p10)}</span>
        <span>p50 {fmtCompact(row.p50)}</span>
        <span>p90 {fmtCompact(row.p90)}</span>
      </div>
      {hasNegativeTail && (
        <p className="mt-1 text-[10px] text-ink-500 dark:text-ink-400">
          Log scale — chart starts at the first positive anchor; some households
          in this cohort have ≤ $0.
        </p>
      )}
    </Card>
  );
}
